import React, { useState } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Clock,
  Brain,
  Shield,
  TrendingDown,
  Lightbulb,
  Target,
  Minus,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Eye,
  Lock,
  Scale,
  Search,
  Layers,
  Star
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────
// ReviewGate
// ─────────────────────────────────────────────────────────────
const ReviewGate = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setShowRejectModal(false);
      setRejectReason('');
    }
  };

  if (execution.status === 'approved') {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-green-700">
          <CheckCircle2 className="w-5 h-5" />
          <span className="font-medium">Execution approvata</span>
        </div>
      </div>
    );
  }

  if (execution.status === 'rejected') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <XCircle className="w-5 h-5" />
          <span className="font-medium">Execution rigettata</span>
        </div>
        {execution.rejectionReason && (
          <p className="text-sm text-red-600 mt-2">{execution.rejectionReason}</p>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-800 mb-3">Revisiona Output</h3>
        <div className="flex gap-3">
          <button onClick={() => onApprove()} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
            <CheckCircle2 className="w-4 h-4" /> Approva
          </button>
          <button onClick={onRegenerate} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
            <RefreshCw className="w-4 h-4" /> Rigenera
          </button>
          <button onClick={() => setShowRejectModal(true)} disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Rigetta
          </button>
        </div>
      </div>

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Motivo del Rigetto</h3>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Spiega perche rigetti questa execution..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
              rows={4} />
            <div className="flex gap-3 mt-4">
              <button onClick={() => setShowRejectModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                Annulla
              </button>
              <button onClick={handleReject} disabled={!rejectReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
                Conferma Rigetto
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────
// FieldCard
// ─────────────────────────────────────────────────────────────
const FieldCard = ({ icon: Icon, label, value, colorClass = 'text-teal-600', bgClass = 'bg-teal-50 border-teal-200' }) => {
  if (!value) return null;
  return (
    <div className={`rounded-lg border p-4 ${bgClass}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${colorClass}`} />
        <span className={`text-xs font-semibold uppercase tracking-wide ${colorClass}`}>{label}</span>
      </div>
      <p className="text-sm text-gray-700 leading-relaxed">{value}</p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// StringList
// ─────────────────────────────────────────────────────────────
const StringList = ({ items, colorClass = 'text-gray-600' }) => {
  if (!items || items.length === 0) return <p className="text-gray-400 text-sm italic">Nessun elemento</p>;
  return (
    <ul className="space-y-1">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-2 text-sm">
          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gray-400 flex-shrink-0" />
          <span className={colorClass}>{typeof item === 'string' ? item : JSON.stringify(item)}</span>
        </li>
      ))}
    </ul>
  );
};

// ─────────────────────────────────────────────────────────────
// AlternativesSection
// ─────────────────────────────────────────────────────────────
const AlternativesSection = ({ label, items = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const validItems = items.filter(Boolean);
  if (!validItems.length) return null;
  return (
    <div className="rounded-lg border border-gray-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Alternatives: {label} ({validItems.length})
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        <div className="p-3 space-y-2">
          {validItems.map((item, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-gray-100 text-xs text-gray-500">{i + 1}</span>
              <p className="text-sm text-gray-600">{typeof item === 'string' ? item : JSON.stringify(item)}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// SecondOrderLatentStatesCard
// ─────────────────────────────────────────────────────────────
const policyColor = (effect) => {
  if (!effect) return 'bg-gray-100 text-gray-600';
  const e = effect.toLowerCase();
  if (e.includes('approach')) return 'bg-green-100 text-green-700';
  if (e.includes('avoid')) return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
};

const strengthColor = (s) => {
  if (!s) return 'bg-gray-100 text-gray-600';
  const v = s.toLowerCase();
  if (v === 'high') return 'bg-red-100 text-red-700';
  if (v === 'medium') return 'bg-yellow-100 text-yellow-700';
  return 'bg-green-100 text-green-700';
};

const SecondOrderLatentStatesCard = ({ states }) => {
  const [expanded, setExpanded] = useState(true);
  if (!states || states.length === 0) return null;
  return (
    <div className="rounded-lg border border-violet-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-violet-50 hover:bg-violet-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-semibold text-violet-800">Second-Order Latent States</span>
          <span className="px-2 py-0.5 text-xs bg-violet-200 text-violet-700 rounded-full">{states.length}</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-violet-400" /> : <ChevronDown className="w-4 h-4 text-violet-400" />}
      </button>
      {expanded && (
        <div className="p-4 space-y-4">
          {states.map((s, i) => (
            <div key={i} className="bg-white border border-violet-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-violet-800">{s.state || `State ${i + 1}`}</span>
                <div className="flex gap-1">
                  {s.expected_strength && (
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${strengthColor(s.expected_strength)}`}>
                      {s.expected_strength}
                    </span>
                  )}
                  {s.policy_effect && (
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${policyColor(s.policy_effect)}`}>
                      {s.policy_effect}
                    </span>
                  )}
                </div>
              </div>
              {s.description && <p className="text-xs text-gray-600 mb-2">{s.description}</p>}
              <div className="grid grid-cols-2 gap-2 text-xs">
                {s.precision_effect && (
                  <div>
                    <p className="text-violet-500 font-medium uppercase tracking-wide mb-0.5">Precision Effect</p>
                    <p className="text-gray-600">{s.precision_effect}</p>
                  </div>
                )}
                {s.likelihood_effect && (
                  <div>
                    <p className="text-violet-500 font-medium uppercase tracking-wide mb-0.5">Likelihood Effect</p>
                    <p className="text-gray-600">{s.likelihood_effect}</p>
                  </div>
                )}
              </div>
              {s.activation_observations && s.activation_observations.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-violet-500 font-medium uppercase tracking-wide mb-1">Activation Observations</p>
                  <StringList items={s.activation_observations} colorClass="text-gray-600" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// JointGenerativeModelCard
// ─────────────────────────────────────────────────────────────
const JointGenerativeModelCard = ({ model }) => {
  const [expanded, setExpanded] = useState(false);
  if (!model) return null;
  const hasContent = (model.first_order_competition?.length > 0)
    || (model.second_order_competition?.length > 0)
    || (model.cross_level_effects?.length > 0);
  if (!hasContent) return null;

  return (
    <div className="rounded-lg border border-indigo-200 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-indigo-50 hover:bg-indigo-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-indigo-600" />
          <span className="text-sm font-semibold text-indigo-800">Joint Generative Model</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-indigo-400" /> : <ChevronDown className="w-4 h-4 text-indigo-400" />}
      </button>
      {expanded && (
        <div className="p-4 space-y-4">
          {model.first_order_competition?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">First-Order Competition</p>
              <StringList items={model.first_order_competition} colorClass="text-gray-700" />
            </div>
          )}
          {model.second_order_competition?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wide mb-2">Second-Order Competition</p>
              <StringList items={model.second_order_competition} colorClass="text-gray-700" />
            </div>
          )}
          {model.cross_level_effects?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-violet-600 uppercase tracking-wide mb-2">Cross-Level Effects</p>
              <StringList items={model.cross_level_effects} colorClass="text-gray-700" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// EmptyState
// ─────────────────────────────────────────────────────────────
const EmptyState = () => (
  <div className="flex items-center justify-center h-full">
    <div className="text-center text-gray-500">
      <Clock className="w-12 h-12 mx-auto mb-4 text-gray-300" />
      <p className="text-lg font-medium mb-2">In attesa di esecuzione</p>
      <p className="text-sm">Clicca "Esegui Contract" per avviare la Latent State & Prior Analysis</p>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────
// Contract03View
// ─────────────────────────────────────────────────────────────
const Contract03View = ({ execution, onApprove, onReject, onRegenerate, loading }) => {
  if (!execution) return <EmptyState />;

  const output = execution.output || execution.outputData || {};

  const hasSecondOrder = (output.second_order_latent_states?.length > 0)
    || output.dominant_second_order_prior
    || output.persuasion_awareness_baseline
    || output.higher_order_robustness_condition;

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto space-y-6">

        {/* Header */}
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Brain className="w-6 h-6 text-teal-600" />
            <h2 className="text-xl font-bold text-gray-800">Latent State & Prior Analysis</h2>
            <span className="px-2 py-0.5 text-xs bg-teal-100 text-teal-700 rounded-full font-medium">C03</span>
          </div>
          <p className="text-sm text-gray-500">Modello inferenziale: la trasformazione cognitiva che la creativita deve indurre</p>
        </div>

        {/* Primary Output */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Output Primario</h3>

          <FieldCard
            icon={Brain}
            label="Creative Latent State"
            value={output.creative_latent_state}
            colorClass="text-teal-700"
            bgClass="bg-teal-50 border-teal-200"
          />
          <FieldCard
            icon={Shield}
            label="Prior Defensive Targeted"
            value={output.prior_defensive_targeted}
            colorClass="text-red-700"
            bgClass="bg-red-50 border-red-200"
          />

          {/* Prior Generating Observations */}
          {output.prior_generating_observations?.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-4 h-4 text-orange-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-orange-700">Prior Generating Observations</span>
              </div>
              <StringList items={output.prior_generating_observations} colorClass="text-gray-700" />
            </div>
          )}

          <FieldCard
            icon={Shield}
            label="Prior Protection Function"
            value={output.prior_protection_function}
            colorClass="text-orange-700"
            bgClass="bg-orange-50 border-orange-200"
          />

          {/* Preferred Outcomes */}
          {output.preferred_outcomes?.length > 0 && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-4 h-4 text-green-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-green-700">Preferred Outcomes</span>
              </div>
              <StringList items={output.preferred_outcomes} colorClass="text-gray-700" />
            </div>
          )}

          <FieldCard
            icon={TrendingDown}
            label="Structural Loss"
            value={output.structural_loss}
            colorClass="text-red-700"
            bgClass="bg-red-50 border-red-200"
          />
          <FieldCard
            icon={Minus}
            label="Desired Belief Update"
            value={output.desired_belief_update}
            colorClass="text-blue-700"
            bgClass="bg-blue-50 border-blue-200"
          />
          <FieldCard
            icon={Lightbulb}
            label="Desired Inference"
            value={output.desired_inference}
            colorClass="text-yellow-700"
            bgClass="bg-yellow-50 border-yellow-200"
          />

          <div className="grid grid-cols-2 gap-3">
            <FieldCard
              icon={AlertTriangle}
              label="Policy - Reduced Ambiguity"
              value={output.policy_reduced_ambiguity}
              colorClass="text-purple-700"
              bgClass="bg-purple-50 border-purple-200"
            />
            <FieldCard
              icon={Target}
              label="Policy - Reduced Risk"
              value={output.policy_reduced_risk}
              colorClass="text-green-700"
              bgClass="bg-green-50 border-green-200"
            />
          </div>
        </div>

        {/* Alternative Latent State S* */}
        {(output.alternative_latent_state || output.s_star_supporting_observations?.length > 0 || output.latent_transition_condition) && (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Alternative Latent State S*</h3>
            <FieldCard
              icon={Target}
              label="Alternative Latent State (S*)"
              value={output.alternative_latent_state}
              colorClass="text-teal-700"
              bgClass="bg-teal-50 border-teal-200"
            />
            {output.s_star_supporting_observations?.length > 0 && (
              <div className="rounded-lg border border-teal-200 bg-teal-50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Search className="w-4 h-4 text-teal-600" />
                  <span className="text-xs font-semibold uppercase tracking-wide text-teal-700">S* Supporting Observations</span>
                </div>
                <StringList items={output.s_star_supporting_observations} colorClass="text-gray-700" />
              </div>
            )}
            <FieldCard
              icon={Target}
              label="Latent Transition Condition"
              value={output.latent_transition_condition}
              colorClass="text-teal-700"
              bgClass="bg-teal-50 border-teal-200"
            />
          </div>
        )}

        {/* Second-Order Analysis */}
        {hasSecondOrder && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Eye className="w-4 h-4 text-violet-600" />
              <h3 className="text-sm font-semibold text-violet-700 uppercase tracking-wide">Second-Order Analysis</h3>
            </div>

            <SecondOrderLatentStatesCard states={output.second_order_latent_states} />

            <FieldCard
              icon={Eye}
              label="Dominant Second-Order Prior"
              value={output.dominant_second_order_prior}
              colorClass="text-violet-700"
              bgClass="bg-violet-50 border-violet-200"
            />
            <FieldCard
              icon={Eye}
              label="Persuasion Awareness Baseline"
              value={output.persuasion_awareness_baseline}
              colorClass="text-violet-700"
              bgClass="bg-violet-50 border-violet-200"
            />
            <FieldCard
              icon={AlertTriangle}
              label="Higher-Order Risk"
              value={output.higher_order_risk}
              colorClass="text-red-700"
              bgClass="bg-red-50 border-red-200"
            />
            <FieldCard
              icon={Lock}
              label="Higher-Order Robustness Condition"
              value={output.higher_order_robustness_condition}
              colorClass="text-violet-700"
              bgClass="bg-violet-50 border-violet-200"
            />
            <div className="grid grid-cols-2 gap-3">
              <FieldCard
                icon={Scale}
                label="Evidence Independence Requirement"
                value={output.evidence_independence_requirement}
                colorClass="text-indigo-700"
                bgClass="bg-indigo-50 border-indigo-200"
              />
              <FieldCard
                icon={Scale}
                label="Epistemic Autonomy Requirement"
                value={output.epistemic_autonomy_requirement}
                colorClass="text-indigo-700"
                bgClass="bg-indigo-50 border-indigo-200"
              />
            </div>

            <JointGenerativeModelCard model={output.joint_generative_model} />
          </div>
        )}

        {/* Alternatives */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Strategie Alternative</h3>
          <AlternativesSection label="Creative Latent State" items={output.alternative_creative_latent_state} />
          <AlternativesSection label="Prior Defensive Targeted" items={output.alternative_prior_defensive_targeted} />
          <AlternativesSection label="Prior Generating Observations" items={output.alternative_prior_generating_observations} />
          <AlternativesSection label="Prior Protection Function" items={output.alternative_prior_protection_function} />
          <AlternativesSection label="Structural Loss" items={output.alternative_structural_loss} />
          <AlternativesSection label="Desired Belief Update" items={output.alternative_desired_belief_update} />
          <AlternativesSection label="Desired Inference" items={output.alternative_desired_inference} />
          <AlternativesSection label="Policy Reduced Ambiguity" items={output.alternative_policy_reduced_ambiguity} />
          <AlternativesSection label="Policy Reduced Risk" items={output.alternative_policy_reduced_risk} />
          <AlternativesSection label="Alternative Latent State S*" items={output.alternative_alternative_latent_state} />
          <AlternativesSection label="S* Supporting Observations" items={output.alternative_s_star_supporting_observations} />
          <AlternativesSection label="Latent Transition Condition" items={output.alternative_latent_transition_condition} />
          <AlternativesSection label="Second-Order Latent States" items={output.alternative_second_order_latent_states} />
          <AlternativesSection label="Higher-Order Robustness Conditions" items={output.alternative_higher_order_robustness_conditions} />
        </div>

        {/* Review Gate */}
        <ReviewGate
          execution={execution}
          onApprove={onApprove}
          onReject={onReject}
          onRegenerate={onRegenerate}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default Contract03View;
